import React, { useCallback, useEffect, useState } from "react";
import { AlertOctagon, ChevronLeft, ChevronRight, RefreshCw, ShieldAlert } from "lucide-react";
import { getECGVendorDashboard } from "./ecgVendorApi";
import { useNavigate } from "react-router-dom";

import DashboardHeader from "./DashboardHeader";
import StatsCards from "./StatsCards";
import ECGFilters from "./ECGFilters";
import ECGCaseTable from "./ECGCaseTable";
import ECGCaseDetails from "./ECGCaseDetails";
import EmptyState from "./EmptyState";

// ---- Loading skeletons -----------------------------------------------

function SkeletonBlock({ className = "" }) {
  return <div className={`animate-pulse bg-[#E5E7EB] rounded ${className}`} />;
}

function DashboardSkeleton() {
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonBlock key={i} className="h-20" />
        ))}
      </div>
      <SkeletonBlock className="h-11" />
      <SkeletonBlock className="h-64" />
      <p className="text-center text-sm text-[#6B7280]">Loading ECG Cases...</p>
    </div>
  );
}

// ---- Error states -------------------------------------------------------

function ErrorPanel({ status, message, onRetry }) {
  if (status === 403) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center bg-white border border-[#FCA5A5] rounded-xl p-8">
        <ShieldAlert className="w-10 h-10 text-[#DC2626] mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-[#1F2937] mb-1">Access restricted</h2>
        <p className="text-sm text-[#6B7280]">
          Your ECG vendor account is not configured or you do not have permission to
          access this dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto mt-16 text-center bg-white border border-[#E5E7EB] rounded-xl p-8">
      <AlertOctagon className="w-10 h-10 text-[#DC2626] mx-auto mb-3" />
      <h2 className="text-lg font-semibold text-[#1F2937] mb-1">
        Unable to load ECG dashboard.
      </h2>
      <p className="text-sm text-[#6B7280] mb-4">{message || "Please try again."}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[#DC2626] text-white hover:bg-[#B91C1C] transition-colors"
      >
        <RefreshCw className="w-4 h-4" />
        Retry
      </button>
    </div>
  );
}

// ---- Pagination bar -------------------------------------------------------

function getPageNumbers(current, total) {
  const delta = 1;
  const range = [];
  const withDots = [];
  let last;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (last) {
      if (i - last === 2) withDots.push(last + 1);
      else if (i - last !== 1) withDots.push("...");
    }
    withDots.push(i);
    last = i;
  }

  return withDots;
}

function PaginationBar({ pagination, onPageChange }) {
  if (!pagination || pagination.total_results === 0) return null;

  const { page, page_size, total_results, total_pages, has_next, has_previous } = pagination;
  const start = (page - 1) * page_size + 1;
  const end = Math.min(page * page_size, total_results);
  const pageNumbers = getPageNumbers(page, total_pages);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
      <p className="text-sm text-[#6B7280]">
        Showing {start} to {end} of {total_results} results
      </p>

      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={!has_previous}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-[#E5E7EB] text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </button>

        {pageNumbers.map((p, idx) =>
          p === "..." ? (
            <span key={`dots-${idx}`} className="px-2 text-sm text-[#9CA3AF]">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg border transition-colors ${
                p === page
                  ? "bg-[#DC2626] border-[#DC2626] text-white"
                  : "border-[#E5E7EB] text-[#374151] hover:bg-[#F9FAFB]"
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!has_next}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-[#E5E7EB] text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---- Main dashboard -------------------------------------------------------

const emptyFilters = { customer: "", status: "", gender: "", test_date: "", search: "" };
const PAGE_SIZE = 10;

export default function ECGVendorDashboard({ onLogout, onUnauthorized, onReportsClick, onToggleTheme }) {
  const [dashboard, setDashboard] = useState(null);
  const [patients, setPatients] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total_cases: 0, reported_cases: 0, pending_cases: 0 });
  const [pagination, setPagination] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // { status, message }

  const [selectedPatient, setSelectedPatient] = useState(null);
  const navigate = useNavigate();
  const handleLogout = onLogout || (() => navigate("/logout"));
  const handleReportsClick = onReportsClick || (() => navigate("/ecg-vendors-reports"));

  const fetchDashboard = useCallback(
    async (activeFilters, pageNumber) => {
      setLoading(true);
      setError(null);
      try {
        const data = await getECGVendorDashboard(activeFilters, pageNumber, PAGE_SIZE);

        if (!data.success) {
          setError({ status: null, message: data.message || "Unable to load ECG dashboard." });
          setLoading(false);
          return;
        }

        setDashboard(data);
        setPatients(data.patients || []);
        setCustomers(data.customers || []);
        setStats(data.stats || { total_cases: 0, reported_cases: 0, pending_cases: 0 });
        setPagination(data.pagination || null);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          if (onUnauthorized) onUnauthorized();
          setError({ status: 401, message: "Session expired. Redirecting to login..." });
        } else if (status === 403) {
          setError({ status: 403, message: err?.response?.data?.message });
        } else {
          setError({
            status: status || 500,
            message: err?.response?.data?.message || "Please try again.",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [onUnauthorized]
  );

  // Initial load: page 1, no filters.
  useEffect(() => {
    fetchDashboard(emptyFilters, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = (draftFilters) => {
    setAppliedFilters(draftFilters);
    setPage(1);
    fetchDashboard(draftFilters, 1);
  };

  const handleClearFilters = () => {
    setAppliedFilters(emptyFilters);
    setPage(1);
    fetchDashboard(emptyFilters, 1);
  };

  const handlePageChange = (newPage) => {
    if (!pagination || newPage < 1 || newPage > pagination.total_pages || newPage === page) return;
    setPage(newPage);
    fetchDashboard(appliedFilters, newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;
  const filtersActive = activeFilterCount > 0;

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <DashboardSkeleton />
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <ErrorPanel
          status={error.status}
          message={error.message}
          onRetry={() => fetchDashboard(appliedFilters, page)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <DashboardHeader
        ecgClient={dashboard?.ecg_client}
        onLogout={handleLogout}
        onReportsClick={handleReportsClick}
        onToggleTheme={onToggleTheme}
      />

      <main className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <StatsCards stats={stats} />

        <ECGFilters
          customers={customers}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          activeCount={activeFilterCount}
        />

        {error && dashboard ? (
          <div className="bg-[#FEF2F2] border border-[#FCA5A5] text-[#B91C1C] text-sm rounded-lg px-4 py-3 flex items-center justify-between">
            <span>{error.message || "Unable to refresh dashboard."}</span>
            <button
              onClick={() => fetchDashboard(appliedFilters, page)}
              className="font-medium underline shrink-0 ml-3"
            >
              Retry
            </button>
          </div>
        ) : null}

        {loading ? (
          <SkeletonBlock className="h-64" />
        ) : patients.length === 0 ? (
          <EmptyState filtersActive={filtersActive} onClearFilters={handleClearFilters} />
        ) : (
          <>
            <ECGCaseTable patients={patients} onView={setSelectedPatient} />
            <PaginationBar pagination={pagination} onPageChange={handlePageChange} />
          </>
        )}
      </main>

      {selectedPatient && (
        <ECGCaseDetails patient={selectedPatient} onClose={() => setSelectedPatient(null)} />
      )}
    </div>
  );
}